import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import * as moment from 'moment';

import { AuthenticationService } from '@app/_services';
import { User } from '@app/_models';
import { TaskService } from '@app/_components/task/task.service';

/**
 * Reservations Dashboard Shell — v2 team-landing page (Phase 2).
 *
 * Sister to OperationsDashboardShellComponent. Follows the same wrapper
 * pattern: pure v2 chrome (banner, greeting, KPI tiles, My Tasks band,
 * surfaced folders) for the Reservations team. Reservations is the
 * comms hub — front-line guest services agents who handle reservations,
 * quotes, refunds, and route messages across owner / bizdev / guest
 * contexts. This dashboard is their daily landing.
 *
 * Route: /reservations-home (registered in app.routing.ts).
 *
 * Phase 2 scaffold scope:
 *   1. Indigo team color stripe (Reservations = #5a6cd6)
 *   2. Greeting + 3 quick-action buttons (New Quote / Tape Chart / Send SMS)
 *   3. KPI tile placeholders — will populate from existing services in
 *      a follow-up commit (open quotes, today's check-ins, refunds pending,
 *      unread inbox counts)
 *   4. Universal "My Tasks" band — wired to TaskService.findByTaskListSummary
 *      using type='me' (same pattern as Ops shell)
 *   5. Multi-context inbox preview placeholder (Guest / Owner / BizDev)
 *   6. Surfaced folder cards (Guests / Ops·Comms / Owners·BizDev)
 *
 * Out of scope for this commit (follow-up):
 *   - Live KPI counts (need backend reservations-dashboard endpoint OR
 *     aggregation across existing services)
 *   - Embedded multi-context inbox preview (today shows placeholder text;
 *     follow-up wires to UnifiedInboxService for top-5 unread per context)
 */
@Component({
    selector: 'app-reservations-dashboard-shell',
    templateUrl: './reservations-dashboard-shell.component.html',
    styleUrls: ['./reservations-dashboard-shell.component.scss'],
    standalone: false,
})
export class ReservationsDashboardShellComponent implements OnInit, OnDestroy {

    /** Time-of-day greeting suffix shown beside the page title. */
    greetingTime = '';

    /** My Tasks — populated from TaskService.findByTaskListSummary(...,'me'). */
    tasks: TaskSummary[] = [];
    tasksLoading = false;
    tasksUrgent = 0;
    tasksOpen = 0;

    private currentUser: User | null = null;
    private subs: Subscription[] = [];

    constructor(
        private authenticationService: AuthenticationService,
        private taskService: TaskService,
    ) {}

    ngOnInit(): void {
        this.greetingTime = this.buildGreeting();

        const userSub = this.authenticationService.currentUser.subscribe(user => {
            this.currentUser = user || null;
            if (this.currentUser && this.currentUser.userId) {
                this.loadTasks();
            }
        });
        this.subs.push(userSub);
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }

    /** "good morning" / "good afternoon" / "good evening" based on local hour. */
    private buildGreeting(): string {
        const hour = moment().hour();
        if (hour < 12) return '· good morning';
        if (hour < 17) return '· good afternoon';
        return '· good evening';
    }

    /**
     * Fetch the current user's open tasks. Same pattern as Ops shell —
     * existing TaskService endpoint, graceful failure leaves empty state.
     */
    private loadTasks(): void {
        if (!this.currentUser || !this.currentUser.userId) return;
        this.tasksLoading = true;

        const sub = this.taskService.findByTaskListSummary(
            0, 5,
            'priority', -1,
            [], '', {},
            this.currentUser.userId,
            'me'
        ).pipe(first()).subscribe({
            next: (res: any) => {
                this.tasksLoading = false;
                const rawTasks = res && res.data && Array.isArray(res.data.tasks) ? res.data.tasks : [];
                this.tasks = rawTasks.map((t: any) => ({
                    taskId:       t.taskId || t.id,
                    priority:     this.normalizePriority(t.priority),
                    description:  t.subject || t.taskName || t.description || '(untitled)',
                    dueDateLabel: this.buildDueLabel(t.dueDate),
                }));
                this.tasksUrgent = this.tasks.filter(t => t.priority === 'URGENT').length;
                this.tasksOpen   = this.tasks.length;
            },
            error: () => {
                this.tasksLoading = false;
            },
        });
        this.subs.push(sub);
    }

    private normalizePriority(p: any): string {
        if (p === 1 || p === '1' || (typeof p === 'string' && p.toUpperCase() === 'URGENT')) return 'URGENT';
        if (p === 2 || p === '2' || (typeof p === 'string' && p.toUpperCase() === 'HIGH'))   return 'HIGH';
        return 'NORMAL';
    }

    /** Date-safety per CLAUDE.md — moment().startOf('day') for date-only math. */
    buildDueLabel(dueDate: string | null | undefined): string {
        if (!dueDate) return '';
        const due  = moment(dueDate).startOf('day');
        const now  = moment().startOf('day');
        const diff = due.diff(now, 'days');
        if (diff < 0)   return 'overdue';
        if (diff === 0) return 'due today';
        if (diff === 1) return 'due tomorrow';
        if (diff <= 6)  return `due ${due.format('dddd')}`;
        return `due ${due.format('MMM D')}`;
    }

    taskPrioClass(priority: string): string {
        switch ((priority || '').toUpperCase()) {
            case 'URGENT': return 'rdsh-task-prio--urgent';
            case 'HIGH':   return 'rdsh-task-prio--high';
            default:       return 'rdsh-task-prio--normal';
        }
    }
}

interface TaskSummary {
    taskId:       number | string;
    priority:     string;
    description:  string;
    dueDateLabel: string;
}
